// Constants and types
import { ComplianceStatus, ComplianceType } from './types';

export const COMPLIANCE = {
    HEADING: 'Customer Compliance Details',
    SUBHEADING:
        'This section provides details on the compliance requirements and issues that apply to your customer account. Use this information to understand and address any compliance-related problems or questions.',

    SECTION_TITLES: {
        DETAILS: 'Details'
    },

    ACCORDION_LABELS: {
        [ComplianceType.OFAC]: 'OFAC',
        [ComplianceType.RED_FLAG]: 'Red Flag',
        [ComplianceType.CONSISTENCY_CHECK]: 'Consistency Check',
        [ComplianceType.ID_VERIFICATION]: 'Document ID',
        [ComplianceType.SYNTHETIC_ID_VERIFICATION]: 'Synthetic ID'
    },

    STATUS_LABELS: {
        PASSED: 'Passed',
        FAILED: 'Failed',
        ERROR: 'Error',
        NOT_STARTED: 'Not Started',
        NOT_APPLICABLE: 'Not Applicable',
        COMPLETED: 'Completed',
        STARTED: 'Started',
        UNDETERMINED: 'Undetermined'
    },

    DEALER_TRACK: {
        LINK_TEXT: 'Dealer Track',
        DESCRIPTION: 'For more detailed compliance information, please visit'
    },

    ERROR_ALERT: {
        TITLE: 'Unable to check Compliance',
        DESCRIPTION: 'Unfortunately, we were unable to provide Customer Compliance details at this time.'
    }
};

export const SYNTHETIC_ID_VERIFICATION = {
    MESSAGES: {
        [ComplianceStatus.PASS]: {
            details: 'There is a low chance of fraud on this order.'
        },
        [ComplianceStatus.FAIL]: {
            details: 'There is a high chance of fraud on this order. Please view the detailed report to fix the reported risks.'
        },
        [ComplianceStatus.NOT_APPLICABLE]: {
            details: 'This compliance check does not apply for the deal.'
        },
        [ComplianceStatus.NOT_STARTED]: {
            details: 'This compliance check has not started for the deal.'
        },
        [ComplianceStatus.STARTED]: {
            details: 'This compliance check has started, results pending.'
        },
        [ComplianceStatus.COMPLETED]: {
            details: 'This compliance check has completed successfully.'
        },
        [ComplianceStatus.UNDETERMINED]: {
            details: 'The synthetic ID verification check completed but the result is undetermined.'
        }
    },

    FRAUD_LEVEL_MESSAGE: {
        getMessage: (level: number) => `Synthetic ID fraud behaviour level ${level} has been detected.`
    }
};

export const COMPLIANCE_ID_VERIFICATION_MESSAGE = {
    MESSAGES: {
        [ComplianceStatus.PASS]: {
            details: 'High confidence that the ID is valid.'
        },
        [ComplianceStatus.FAIL]: {
            details: 'Low confidence that the ID is valid.'
        },
        [ComplianceStatus.NOT_APPLICABLE]: {
            details: 'This compliance check does not apply for the deal.'
        },
        [ComplianceStatus.NOT_STARTED]: {
            details: 'This compliance check has not started for the deal.'
        },
        [ComplianceStatus.STARTED]: {
            details: 'This compliance check has started, results pending.'
        },
        [ComplianceStatus.COMPLETED]: {
            details: 'This compliance check has completed successfully.'
        },
        [ComplianceStatus.UNDETERMINED]: {
            details: 'The document ID verification check completed but the result is undetermined.'
        }
    }
};

export const COMPLIANCE_OFAC_MESSAGE = {
    MESSAGES: {
        [ComplianceStatus.PASS]: {
            title: 'No OFAC Violations Were Found',
            description: 'Please view detailed compliance information in DealerTrack.'
        },
        [ComplianceStatus.FAIL]: {
            title: 'OFAC Compliance Issue Detected',
            description:
                'The buyer has failed OFAC compliance. This order has been placed on hold due to the sensitive nature of this matter.'
        },
        [ComplianceStatus.NOT_APPLICABLE]: {
            title: '',
            description: 'This compliance check does not apply for the deal.'
        },
        [ComplianceStatus.NOT_STARTED]: {
            title: '',
            description: 'This compliance check has not started for the deal.'
        },
        [ComplianceStatus.STARTED]: {
            title: '',
            description: 'This compliance check has started, results pending.'
        },
        [ComplianceStatus.COMPLETED]: {
            title: '',
            description: 'This compliance check has completed successfully.'
        }
    }
};

export const COMPLIANCE_CONSISTENCY_CHECK_MESSAGE = {
    MESSAGES: {
        [ComplianceStatus.PASS]: {
            title: 'No PII consistency issues found',
            description: 'Please view detailed compliance information in DealerTrack.'
        },
        [ComplianceStatus.FAIL]: {
            title: 'Provided information is not matching',
            description: ''
        }
    }
};

// Column name mapping for display in consistency check
export const comparisonValues: Record<string, string> = {
    idfs: "Driver's License",
    commerce: 'Credit App',
    mq: 'MagiQual'
};

export const COMPLIANCE_RED_FLAG_MESSAGE = {
    MESSAGES: {
        [ComplianceStatus.PASS]: {
            title: 'No Red flag Violations Were Found',
            description: 'Please view detailed compliance information in DealerTrack.',
            details: ''
        },
        [ComplianceStatus.FAIL]: {
            title: '',
            description: '',
            details: 'Below, you can view a list of Red Flag issues that need to be resolved: '
        },
        [ComplianceStatus.NOT_APPLICABLE]: {
            title: '',
            description: '',
            details: 'This compliance check does not apply for the deal.'
        },
        [ComplianceStatus.NOT_STARTED]: {
            title: '',
            description: '',
            details: 'This compliance check has not started for the deal.'
        },
        [ComplianceStatus.STARTED]: {
            title: '',
            description: '',
            details: 'This compliance check has started, results pending.'
        },
        [ComplianceStatus.COMPLETED]: {
            title: '',
            description: '',
            details: 'This compliance check has completed successfully.'
        }
    }
};

export const COMPLIANCE_ALERT_MESSAGES = {
    [ComplianceStatus.PASS]: {
        title: 'OFAC/Red Flag Passed',
        description: 'New customer compliance documents received -passed related copy'
    },
    [ComplianceStatus.FAIL]: {
        title: 'OFAC/Red Flag Compliance Failure',
        description: 'The system has detected a potential OFAC or Red Flag violation.'
    }
};
export const DOCUMENT_VERIFICATION_TRUST_REASONS_KEY = 'documentVerificationTrustReasons';
export const DOCUMENT_VERIFICATION_TRUST_KEY = 'documentVerificationTrust';

export const ASSESSMENT_LEVEL_KEY = 'assessmentLevel';
export const FINAL_ASSESSMENT_KEY = 'finalAssessment';

export const RESEND_ID_REQUEST_CONFIRMATION = 'Re-send Id Request to the customer?';
export const RESEND_ID_REQUEST_ACTION_LABEL = 'Send';
export const RESEND_ID_REQUEST_PHONE_PLACEHOLDER = 'Phone Input';

export const ID_VERIFICATION_ERROR_ALERT = {
    TITLE: 'Unable to re-send ID request',
    DESCRIPTION: 'Unfortunately, we were unable to handle ID request at this time.'
};

export const ID_VERIFICATION_SUCCESS_ALERT = {
    TITLE: 'The ID Verification request has been re-sent to the customer successfully.'
};
